import FieldTypeSelect from "./FieldTypeSelect";
/**
 * DocumentTypeDialog · parts extraites — spec iter208bis (2026-02-28).
 *
 * Regroupe les composants purement présentiels utilisés uniquement par
 * `DocumentTypeDialog.jsx` :
 *   • MemoryCountPastille (pastille "N valeurs mémorisées")
 *   • FieldRow            (ligne d'un DocumentField dans la liste)
 *   • StepCard            (carte d'une étape workflow)
 *   • DeadlineSentence    (phrase naturelle du délai d'étape)
 *   • ToggleSwitch        (petit switch on/off)
 *
 * L'extraction est chirurgicale : aucune modification de rendu ni de
 * logique n'a été introduite. Le fichier principal descend de ~2700
 * lignes à ~1900 lignes.
 */
import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, GripVertical, MapPin, Calendar, ListChecks, FileText, ChevronDown, ChevronRight, Users, Bookmark, Settings } from "lucide-react";
import { toast } from "@/lib/toast";
import StepResponsibleSelect from "@/components/documents/StepResponsibleSelect";
import api from "@/lib/api";
import UserAvatar from "@/components/ui/UserAvatar";
import ManageMemoryValuesDialog from "@/components/documents/ManageMemoryValuesDialog";
import {
  REQUIRED_FIELD_TYPES,
  DOC_FIELD_TYPES,
  CREATION_ANCHOR,
  FRESH_MS,
} from "@/components/documents/DocumentTypeDialogConsts";

// Spec iter215ter (2026-02-28) — Copie locale du helper `_slug`
// (initialement défini dans DocumentTypeDialog.jsx) pour permettre
// à FieldRow d'auto-slugifier `key` quand on tape le label. La copie
// évite un import circulaire (Parts ← Dialog ← Parts) tout en gardant
// une SEULE définition sémantiquement (aucune divergence de règle).
const _slug = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "champ";

export function MemoryCountPastille({ count, index, typeId, fieldKey }) {
  const [open, setOpen] = React.useState(false);
  const [top, setTop] = React.useState(null);      // null = pas encore chargé
  const [loading, setLoading] = React.useState(false);

  const onEnter = async () => {
    setOpen(true);
    if (top !== null || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/document-types/${typeId}/field-memory/top`, {
        params: { field_key: fieldKey, limit: 5 },
      });
      setTop(data?.top || []);
    } catch {
      setTop([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span
      className="relative inline-flex items-center justify-center rounded-full text-[9px] font-bold px-1.5 cursor-default"
      style={{
        height: 15,
        minWidth: 18,
        background: "rgba(43,87,154,0.20)",
        color: "#8AB8E8",
        border: "1px solid rgba(43,87,154,0.35)",
        lineHeight: 1,
      }}
      data-testid={`doctype-field-memory-count-${index}`}
      onMouseEnter={onEnter}
      onMouseLeave={() => setOpen(false)}
    >
      {count}
      {open && (
        <div
          className="absolute bottom-full right-0 mb-1 rounded-md whitespace-nowrap"
          style={{
            background: "#1A1A1A",
            border: "1px solid #333",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            padding: "6px 8px",
            zIndex: 9999,
            minWidth: 150,
          }}
        >
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            {loading ? "Chargement…" : "Top 5 mémorisées"}
          </div>
          {!loading && (top || []).length === 0 && (
            <div className="text-[10px] text-muted-foreground italic">(aucune)</div>
          )}
          {(top || []).map((row, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-[10px]" style={{ color: "#E5E5E5" }}>
              <span className="truncate max-w-[180px]">{row.value}</span>
              <span className="font-mono text-[9px]" style={{ color: "#8AB8E8" }}>×{row.count}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

export function FieldRow({
  index, field, typeId = null, locked = false, onUpdate, onRemove,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, isDragOver,
}) {
  const [manageOpen, setManageOpen] = useState(false);
  const isMemory = field.type === "text_memory";
  // Spec iter164w (2026-02-26) — Pastille avec le nombre de valeurs déjà
  // mémorisées à côté de l'icône ⚙. Fetch au montage + à chaque fermeture
  // de la modale de gestion (pour refléter les renommages/fusions).
  const [memCount, setMemCount] = useState(null);
  useEffect(() => {
    if (!isMemory || !typeId || !field.key) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/document-types/${typeId}/field-memory`, {
          params: { field_key: field.key },
        });
        if (alive) setMemCount((data?.values || []).length);
      } catch {
        if (alive) setMemCount(null);
      }
    })();
    return () => { alive = false; };
  }, [isMemory, typeId, field.key, manageOpen]);
  return (
    <div
      className="rounded-md"
      style={{
        background: locked ? "#1D2530" : "#202020",
        border: `1px solid ${isDragOver ? "#2B579A" : (locked ? "#2B579A55" : "#2A2A2A")}`,
        transition: "border-color 120ms ease",
      }}
      onDragOver={locked ? undefined : onDragOver}
      onDragLeave={onDragLeave}
      onDrop={locked ? undefined : onDrop}
      onDragEnd={onDragEnd}
      data-testid={`doctype-field-row-${index}`}
    >
      <div
        className="grid items-center gap-2 px-2.5 py-1.5"
        style={{ gridTemplateColumns: "20px 1fr 160px auto 40px" }}
      >
        <div
          draggable={!locked}
          onDragStart={locked ? undefined : onDragStart}
          className={locked
            ? "text-muted-foreground/40 select-none"
            : "cursor-grab active:cursor-grabbing text-muted-foreground hover:text-white transition-colors select-none"}
          title={locked ? "Champ verrouillé — position fixe" : "Glisser pour réordonner"}
          data-testid={`doctype-field-handle-${index}`}
        >
          {locked ? <span style={{ fontSize: 12 }}>🔒</span> : <GripVertical size={14} />}
        </div>

        {/* Spec iter164t (2026-02-25) — Icône engrenage « Gérer les
            valeurs mémorisées » repositionnée À L'INTÉRIEUR du champ
            Nom du champ (au lieu du lien texte séparé en-dessous).
            Visible uniquement sur un champ text_memory d'un TYPE
            EXISTANT (typeId défini). */}
        <div className="relative">
          <Input
            value={field.label}
            onChange={(e) => locked ? undefined : onUpdate({ label: e.target.value, key: _slug(e.target.value) })}
            placeholder="Nom du champ (ex : Nom et prénom du client)"
            className="h-8"
            disabled={locked}
            data-testid={`doctype-field-label-${index}`}
            style={{
              background: "#1B1B1B",
              opacity: locked ? 0.85 : 1,
              paddingRight: (isMemory && !locked && typeId) ? ((memCount && memCount > 0) ? 60 : 32) : undefined,
            }}
          />
          {isMemory && !locked && typeId && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {/* Spec iter164w — pastille comptage valeurs mémorisées.
                  Spec iter164J (2026-02-26) — au survol, mini-popover
                  affiche les 5 valeurs les plus fréquentes. */}
              {memCount !== null && memCount > 0 && (
                <MemoryCountPastille
                  count={memCount}
                  index={index}
                  typeId={typeId}
                  fieldKey={field.key}
                />
              )}
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                className="inline-flex items-center justify-center rounded transition-colors"
                style={{
                  width: 22, height: 22,
                  color: "#8A8A8F",
                  background: "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#5A9EE0";
                  e.currentTarget.style.background = "rgba(43,87,154,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#8A8A8F";
                  e.currentTarget.style.background = "transparent";
                }}
                title="Gérer les valeurs mémorisées"
                data-testid={`doctype-field-manage-memory-${index}`}
              >
                <Settings size={14} strokeWidth={2.2} />
              </button>
            </div>
          )}
        </div>

        <FieldTypeSelect value={field.type || "text"} onChange={(v) => onUpdate({ type: v })} disabled={locked} style={{ opacity: locked ? 0.85 : 1 }} testId={`doctype-field-type-${index}`} />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => locked ? undefined : onUpdate({ required: !field.required })}
            className="relative inline-flex h-4 w-8 shrink-0 rounded-full transition-colors"
            style={{
              background: field.required ? "#2B579A" : "#3A3A3A",
              cursor: locked ? "not-allowed" : "pointer",
              opacity: locked ? 0.7 : 1,
            }}
            role="switch"
            aria-checked={field.required}
            disabled={locked}
            data-testid={`doctype-field-required-${index}`}
            title={locked ? "Verrouillé : toujours obligatoire" : "Obligatoire à la saisie"}
          >
            <span
              className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all"
              style={{ left: field.required ? 18 : 2 }}
            />
          </button>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Obligatoire</span>
        </div>

        {locked ? (
          <span
            className="h-8 w-8 rounded-full inline-flex items-center justify-center flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider select-none"
            style={{ color: "#5A9EE0", background: "rgba(90,158,224,0.10)", border: "1px solid rgba(90,158,224,0.25)" }}
            title="Champ verrouillé — Titre obligatoire du document"
            data-testid={`doctype-field-locked-${index}`}
          >
            📌
          </span>
        ) : (
          <button
            type="button"
            onClick={onRemove}
            className="h-8 w-8 rounded-full inline-flex items-center justify-center flex-shrink-0 transition-colors"
            style={{ color: "#EF4444" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.15)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            data-testid={`doctype-field-remove-${index}`}
            title="Supprimer le champ"
          >
            <Trash2 size={14} strokeWidth={2.2} />
          </button>
        )}
      </div>

      {/* Dropdown options — full-width sub-row visible only for dropdown type */}
      {field.type === "dropdown" && !locked && (
        <div className="px-2.5 pb-2 pl-9">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            Options (séparées par une virgule)
          </div>
          <Input
            value={(field.options || []).join(", ")}
            onChange={(e) => onUpdate({ options: e.target.value.split(",") })}
            placeholder="Ex : BEA, BNA, CPA, BADR"
            className="h-7 text-xs"
            data-testid={`doctype-field-options-${index}`}
            style={{ background: "#1B1B1B" }}
          />
        </div>
      )}

      {/* Spec iter164t (2026-02-25) — bouton « Gérer les valeurs
          mémorisées » retiré d'ici, désormais rendu en tant qu'icône
          engrenage à L'INTÉRIEUR du champ Nom du champ (voir plus haut
          dans ce composant, juste après le grip et l'input). */}

      {isMemory && typeId && (
        <ManageMemoryValuesDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          typeId={typeId}
          fieldKey={field.key}
          fieldLabel={field.label}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// StepCard — one workflow step. Left zone = required field for closure,
// right zone = deadline configuration expressed as a natural-language
// sentence. Card style per spec: #242424 bg, blue left border, radius 10.
// ---------------------------------------------------------------------------
export function StepCard({
  index, step, earlierSteps, onUpdate, onRemove,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, isDragOver,
  justAdded,
}) {
  const s = step;
  const num = String(index + 1).padStart(2, "0");
  // Spec iter77 (2026-02-XX) — 3ème option "none" (Sans délai) : quand
  // sélectionnée, l'étape n'a AUCUNE échéance (deadline_required=false,
  // deadline_mode=null). Cas d'usage : timing dépendant d'un tiers externe
  // (ex: accusé notaire) qui varie trop pour un délai fixe/relatif.
  const noDeadline = !s.deadline_required && !s.deadline_mode;
  const useFromStep = s.deadline_mode !== "fixed";
  const labelInputRef = useRef(null);

  // Spec 2026-02-XX rev50 — Drag & drop reordering pour les champs
  // requis à l'intérieur d'une étape. État LOCAL (une carte à la fois).
  // La poignée ⋮⋮ (GripVertical) est cohérente avec celle des étapes.
  const [rfDragFromIdx, setRfDragFromIdx] = useState(null);
  const [rfDragOverIdx, setRfDragOverIdx] = useState(null);
  const onRfDragStart = (i) => (e) => {
    setRfDragFromIdx(i);
    e.stopPropagation();  // ne PAS déclencher le drag de la carte étape parente
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", `rf:${i}`); } catch (_) {}
  };
  const onRfDragOver = (i) => (e) => {
    if (rfDragFromIdx == null) return;
    e.preventDefault(); e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (rfDragOverIdx !== i) setRfDragOverIdx(i);
  };
  const onRfDrop = (targetIdx) => (e) => {
    if (rfDragFromIdx == null) return;
    e.preventDefault(); e.stopPropagation();
    const from = rfDragFromIdx;
    setRfDragFromIdx(null); setRfDragOverIdx(null);
    if (from === targetIdx) return;
    const list = [...(s.required_fields || [])];
    const [moved] = list.splice(from, 1);
    list.splice(targetIdx, 0, moved);
    onUpdate({ required_fields: list });
  };
  const onRfDragEnd = () => { setRfDragFromIdx(null); setRfDragOverIdx(null); };

  // Focus the label input automatically when the step is just added.
  useEffect(() => {
    if (justAdded && labelInputRef.current) {
      labelInputRef.current.focus();
    }
  }, [justAdded]);

  const accentColor = justAdded ? "#22C55E" : "#2B579A";

  return (
    <div
      className="relative rounded-[10px] overflow-hidden"
      style={{
        background: "#242424",
        borderLeft: `3px solid ${accentColor}`,
        boxShadow: isDragOver
          ? `0 0 0 2px ${accentColor}, 0 4px 16px rgba(43,87,154,0.35)`
          : (justAdded
              ? "0 0 0 1px rgba(34,197,94,0.35), 0 4px 16px rgba(34,197,94,0.15)"
              : "0 2px 12px rgba(0,0,0,0.25)"),
        transition: "box-shadow 120ms ease, border-color 200ms ease",
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`doctype-step-row-${index}`}
    >
      {/* ---- Header row --------------------------------------------------- */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-3">
        {/* Drag handle — only element that starts the drag operation. */}
        <div
          draggable
          onDragStart={onDragStart}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-white transition-colors select-none"
          title="Glisser pour réordonner"
          data-testid={`doctype-step-handle-${index}`}
        >
          <GripVertical size={18} />
        </div>

        {/* Numbered badge — gradient per spec (#2B579A → #1E3E70). */}
        <div
          className="inline-flex items-center justify-center h-8 min-w-[36px] rounded-md text-xs font-bold tabular-nums px-2 select-none"
          style={{
            background: "linear-gradient(135deg, #2B579A 0%, #1E3E70 100%)",
            color: "#fff",
            boxShadow: "0 2px 6px rgba(43,87,154,0.35)",
          }}
        >
          {num}
        </div>

        <Input
          ref={labelInputRef}
          value={s.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder="Nom de l'étape (ex : Demande d'établissement d'acte)"
          className="h-9 flex-1"
          data-testid={`doctype-step-label-${index}`}
          style={{ background: "#1B1B1B" }}
        />

        {justAdded && (
          <span
            className="inline-flex items-center h-6 px-2 rounded-full text-[10px] font-semibold uppercase tracking-widest select-none"
            style={{
              background: "rgba(34,197,94,0.15)",
              color: "#22C55E",
              border: "1px solid rgba(34,197,94,0.35)",
            }}
            data-testid={`doctype-step-fresh-badge-${index}`}
          >
            Nouvelle
          </span>
        )}

        {/* Delete — bigger, high-contrast red, hover-tinted background. */}
        <button
          type="button"
          onClick={onRemove}
          className="h-10 w-10 rounded-full inline-flex items-center justify-center flex-shrink-0 transition-colors"
          style={{ color: "#EF4444" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.15)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          data-testid={`doctype-step-remove-${index}`}
          title="Supprimer l'étape"
        >
          <Trash2 size={17} strokeWidth={2.2} />
        </button>
      </div>

      {/* ---- Body: header (Responsable) + two-zone split ---------------- */}
      <div className="px-4 pb-4 space-y-3">
        {/* Spec iter123 pt.1 (Feb 2026) — layout STACKED : label au-dessus
            du champ, style unifié 11 px / #e5e5e7 / 700 / 0.4 px, sans
            icône silhouette (retirée dans StepResponsibleSelect). */}
        <div>
          <div
            style={{
              color: "#e5e5e7",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Responsable
          </div>
          <StepResponsibleSelect
            values={Array.isArray(s.responsible_user_ids)
              ? s.responsible_user_ids
              : (s.responsible_user_id ? [s.responsible_user_id] : [])}
            onChange={(uids) => onUpdate({ responsible_user_ids: uids })}
            testId={`doctype-step-responsible-${index}`}
          />
        </div>

        <div className="grid grid-cols-[1fr_1px_1fr] gap-4">
          {/* ---- LEFT zone: required fields for closure (list) ----------- */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                Champs requis pour clôturer
                {" · "}
                <span className="text-muted-foreground">
                  {(s.required_fields || []).length}
                </span>
              </div>
            <button
              type="button"
              onClick={() => {
                const rf = [...(s.required_fields || []), {
                  id: `new-rf-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                  label: `Champ ${(s.required_fields || []).length + 1}`,
                  type: "text",
                  options: [],
                  active: true,
                }];
                onUpdate({ required_fields: rf });
              }}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors hover:brightness-110"
              style={{
                background: "rgba(74,140,216,0.22)",
                color: "#8AB8E8",
                border: "1px solid rgba(74,140,216,0.55)",
              }}
              data-testid={`step-${index}-add-required-field`}
              title="Ajouter un champ requis"
            >
              <Plus size={12} strokeWidth={2.4} /> Ajouter un champ
            </button>
          </div>

          <div className="space-y-1.5">
            {(s.required_fields || []).map((f, fi) => {
              const patchField = (patch) => {
                const next = [...(s.required_fields || [])];
                next[fi] = { ...next[fi], ...patch };
                onUpdate({ required_fields: next });
              };
              const removeField = () => {
                const next = (s.required_fields || []).filter((_, k) => k !== fi);
                onUpdate({ required_fields: next });
              };
              const canRemove = (s.required_fields || []).length > 1;
              return (
                <div
                  key={f.id || fi}
                  className="rounded-md p-2"
                  style={{
                    background: "#1D1D1D",
                    border: "1px solid " + (rfDragOverIdx === fi ? "rgba(90,158,224,0.55)" : "rgba(255,255,255,0.06)"),
                    boxShadow: rfDragOverIdx === fi ? "0 0 0 1px rgba(90,158,224,0.35)" : "none",
                    opacity: rfDragFromIdx === fi ? 0.5 : 1,
                    transition: "border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
                  }}
                  draggable
                  onDragStart={onRfDragStart(fi)}
                  onDragOver={onRfDragOver(fi)}
                  onDragLeave={() => setRfDragOverIdx(null)}
                  onDrop={onRfDrop(fi)}
                  onDragEnd={onRfDragEnd}
                  data-testid={`step-${index}-rf-row-${fi}`}
                >
                  <div className="grid items-center gap-2" style={{ gridTemplateColumns: "16px 1fr 130px auto 32px" }}>
                    {/* Poignée de drag & drop — spec rev50, cohérente avec
                        celle des étapes. Cursor grab / grabbing. */}
                    <span
                      className="text-muted-foreground select-none inline-flex items-center justify-center"
                      style={{
                        cursor: "grab", width: 16, height: 24,
                        color: "#6B7280",
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.cursor = "grabbing"; }}
                      onMouseUp={(e) => { e.currentTarget.style.cursor = "grab"; }}
                      title="Glisser pour réordonner"
                      data-testid={`step-${index}-rf-${fi}-drag-handle`}
                      aria-label="Poignée de réorganisation"
                    >
                      <GripVertical size={14} />
                    </span>
                    <Input
                      value={f.label || ""}
                      onChange={(e) => patchField({ label: e.target.value })}
                      placeholder="Nom du champ (ex : Scan du chèque)"
                      className="h-8 text-xs"
                      data-testid={`step-${index}-rf-${fi}-label`}
                      style={{ background: "#1B1B1B" }}
                    />
                    <FieldTypeSelect value={f.type || "text"} onChange={(v) => patchField({ type: v })} className="h-8 text-xs" testId={`step-${index}-rf-${fi}-type`} />
                    <button
                      type="button"
                      onClick={() => patchField({ active: !f.active })}
                      className="relative inline-flex h-4 w-8 shrink-0 rounded-full transition-colors"
                      style={{ background: f.active ? "#2B579A" : "#3A3A3A" }}
                      role="switch"
                      aria-checked={f.active}
                      data-testid={`step-${index}-rf-${fi}-active`}
                      title={f.active
                        ? "Actif — devra être rempli pour clôturer"
                        : "Optionnel — clôture possible même vide"}
                    >
                      <span
                        className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all"
                        style={{ left: f.active ? 18 : 2 }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={canRemove ? removeField : undefined}
                      disabled={!canRemove}
                      className="h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors"
                      style={{
                        color: canRemove ? "#EF4444" : "#3A3A3A",
                        cursor: canRemove ? "pointer" : "not-allowed",
                      }}
                      onMouseEnter={(e) => canRemove && (e.currentTarget.style.background = "rgba(239,68,68,0.15)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      data-testid={`step-${index}-rf-${fi}-remove`}
                      title={canRemove
                        ? "Supprimer ce champ"
                        : "Au moins un champ requis est nécessaire"}
                    >
                      <Trash2 size={13} strokeWidth={2.2} />
                    </button>
                  </div>
                  {f.type === "dropdown" && (
                    <Input
                      value={(f.options || []).join(", ")}
                      onChange={(e) => patchField({ options: e.target.value.split(",") })}
                      placeholder="Options (ex : BEA, BNA, CPA)"
                      className="h-7 text-xs mt-1.5"
                      data-testid={`step-${index}-rf-${fi}-options`}
                      style={{ background: "#1B1B1B" }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-[10px] text-muted-foreground italic pt-1">
            L&apos;étape ne sera clôturable que lorsque tous les champs marqués « actifs » seront remplis.
          </div>
        </div>

        {/* ---- Vertical separator ---------------------------------------- */}
        <div className="w-px" style={{ background: "rgba(255,255,255,0.08)" }} />

        {/* ---- RIGHT zone: deadline configuration ------------------------ */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Délai de l&apos;étape
          </div>

          <div
            className="grid grid-cols-3 rounded-md overflow-hidden text-xs"
            style={{ background: "#1B1B1B", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            {[
              { v: "from_step", l: "Depuis une étape" },
              { v: "fixed",     l: "Date fixe" },
              { v: "none",      l: "Sans délai" },
            ].map((t) => {
              const on = t.v === "none"
                ? noDeadline
                : (!noDeadline && (s.deadline_mode || "from_step") === t.v);
              return (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => {
                    if (t.v === "none") {
                      // Sans délai : force required=false + mode=null + purge
                      // les valeurs de délai relatif/fixe pour un state propre.
                      onUpdate({
                        deadline_required: false,
                        deadline_mode: null,
                        deadline_from_step_id: null,
                        deadline_days: 0,
                        deadline_fixed: null,
                      });
                    } else {
                      // Passer d'un mode "none" à un mode réel réactive
                      // automatiquement le toggle Obligatoire pour éviter
                      // de laisser un mode vide.
                      const wasNone = noDeadline;
                      onUpdate({
                        deadline_mode: t.v,
                        ...(wasNone ? { deadline_required: true } : {}),
                      });
                    }
                  }}
                  className="py-1.5 font-medium transition-colors"
                  style={{
                    background: on ? "#2B579A" : "transparent",
                    color: on ? "#fff" : "#9CA3AF",
                  }}
                  data-testid={`step-${index}-deadline-mode-${t.v}`}
                >
                  {t.l}
                </button>
              );
            })}
          </div>

          {noDeadline ? (
            <div
              className="text-[11px] text-muted-foreground italic px-2 py-1.5 rounded-md"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.08)" }}
              data-testid={`step-${index}-no-deadline-hint`}
            >
              Aucune échéance calculée pour cette étape. Aucune alerte de retard
              ne sera générée sur les documents utilisant ce type.
            </div>
          ) : useFromStep ? (
            <DeadlineSentence
              index={index}
              step={s}
              earlierSteps={earlierSteps}
              onUpdate={onUpdate}
            />
          ) : (
            <Input
              type="date"
              value={s.deadline_fixed || ""}
              onChange={(e) => onUpdate({ deadline_fixed: e.target.value })}
              className="h-9"
              data-testid={`step-${index}-deadline-fixed`}
              style={{ background: "#1B1B1B" }}
            />
          )}

          <ToggleSwitch
            checked={!!s.deadline_required}
            onChange={(v) => onUpdate({
              deadline_required: v,
              deadline_mode: v ? (s.deadline_mode || "from_step") : null,
            })}
            disabled={noDeadline}
            label="Obligatoire"
            hint={noDeadline
              ? "Désactivé automatiquement en mode « Sans délai »."
              : s.deadline_required
                ? "Un délai doit être défini pour cette étape."
                : "Délai optionnel — peut être laissé vide."}
            testid={`step-${index}-deadline-required`}
          />
        </div>
      </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// DeadlineSentence — natural-language editor for the from-step mode.
//
// Spec 2026-02-XX rev8 (Feb 2026) — refonte du sélecteur d'ancre : chaque
// étape (même la 1ʳᵉ) affiche maintenant une VRAIE liste déroulante avec :
//   1. « 📅 Création du document » — toujours disponible
//   2. Une option par étape précédente (« 01 Signature », « 02 Envoi », …)
//
// Cela permet à une étape N de pointer directement vers la création du
// document (deadline fixe légale) au lieu d'être forcée de dépendre de la
// clôture de l'étape N-1. Sélection par défaut : étape 1 → Création,
// étape N>1 → étape N-1 (préserve le comportement historique). L'ancien
// affichage à pills (`[01] [02]`) est supprimé.
//
// Rendu :
//   [ NN ] jours après [────── SELECT ──────▾]
// ---------------------------------------------------------------------------
export function DeadlineSentence({ index, step, earlierSteps, onUpdate }) {
  // Résout la valeur "effective" du select : la valeur stockée si présente,
  // sinon la valeur par défaut sensée (Création pour étape 1, dernière
  // étape précédente sinon). On propage ce défaut dans l'état parent au
  // premier rendu pour que la sauvegarde reflète ce qui est affiché.
  const defaultAnchor = earlierSteps.length === 0
    ? CREATION_ANCHOR
    : (() => {
        const last = earlierSteps[earlierSteps.length - 1];
        return last.id && !String(last.id).startsWith("new-")
          ? last.id
          : `__idx__:${earlierSteps.length - 1}`;
      })();

  const currentValue = step.deadline_from_step_id || defaultAnchor;

  // Si aucune ancre n'est stockée mais qu'un défaut est calculé, on le
  // matérialise dans l'état parent — évite qu'une simple validation sans
  // interaction ne parte au serveur avec une ancre nulle.
  useEffect(() => {
    if (!step.deadline_from_step_id && defaultAnchor) {
      onUpdate({ deadline_from_step_id: defaultAnchor });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Libellé d'un item — utilisé aussi pour le trigger.
  const labelFor = (value) => {
    if (value === CREATION_ANCHOR) return "Création du document";
    // idx sentinel from re-ordering path
    if (String(value).startsWith("__idx__:")) {
      const j = parseInt(String(value).split(":")[1], 10);
      const s = earlierSteps[j];
      const nn = String(j + 1).padStart(2, "0");
      return s ? `${nn}  ${s.label || "(sans nom)"}` : `${nn}`;
    }
    const j = earlierSteps.findIndex((s) => s.id === value);
    if (j >= 0) {
      const nn = String(j + 1).padStart(2, "0");
      return `${nn}  ${earlierSteps[j].label || "(sans nom)"}`;
    }
    return "—";
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Input
        type="number"
        min={0}
        value={step.deadline_days ?? 0}
        onChange={(e) => onUpdate({
          deadline_days: Math.max(0, parseInt(e.target.value || "0", 10)),
        })}
        className="h-8 w-[70px] text-center pr-1"
        data-testid={`step-${index}-deadline-days`}
        style={{ background: "#1B1B1B" }}
      />
      <span className="text-muted-foreground">jours après</span>

      <Select
        value={currentValue}
        onValueChange={(v) => onUpdate({ deadline_from_step_id: v })}
      >
        <SelectTrigger
          className="h-8 w-[240px] text-xs font-medium"
          style={{
            background: "#1B1B1B",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
          data-testid={`step-${index}-deadline-anchor-trigger`}
        >
          <SelectValue>
            <span className="inline-flex items-center gap-1.5 truncate">
              {currentValue === CREATION_ANCHOR && (
                <Calendar size={12} strokeWidth={2.4} style={{ color: "#5A9EE0" }} />
              )}
              <span className="truncate">{labelFor(currentValue)}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent
          className="bg-[#242424] border-[#3A3A3A] text-white min-w-[240px]"
          data-testid={`step-${index}-deadline-anchor-content`}
        >
          <SelectItem
            value={CREATION_ANCHOR}
            className="text-xs font-medium"
            data-testid={`step-${index}-deadline-anchor-item-creation`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={12} strokeWidth={2.4} style={{ color: "#5A9EE0" }} />
              Création du document
            </span>
          </SelectItem>
          {earlierSteps.length > 0 && (
            <div className="h-px my-1" style={{ background: "rgba(255,255,255,0.08)" }} />
          )}
          {earlierSteps.map((prev, j) => {
            const value = prev.id && !String(prev.id).startsWith("new-")
              ? prev.id
              : `__idx__:${j}`;
            const nn = String(j + 1).padStart(2, "0");
            return (
              <SelectItem
                key={value}
                value={value}
                className="text-xs"
                data-testid={`step-${index}-deadline-anchor-item-${j}`}
              >
                <span className="inline-flex items-center gap-2">
                  <span className="tabular-nums font-semibold" style={{ color: "#5A9EE0" }}>
                    {nn}
                  </span>
                  <span className="truncate max-w-[180px]">
                    {prev.label || "(sans nom)"}
                  </span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}


// ---------------------------------------------------------------------------
// ToggleSwitch — small on/off switch used for "Actif" and "Obligatoire".
// ---------------------------------------------------------------------------
export function ToggleSwitch({ checked, onChange, label, hint, testid, disabled }) {
  return (
    <label className={`flex items-center gap-2.5 select-none ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className="relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed"
        style={{ background: checked ? "#2B579A" : "#3A3A3A" }}
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        data-testid={testid}
      >
        <span
          className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
          style={{ left: checked ? 18 : 2 }}
        />
      </button>
      <div className="min-w-0">
        <div className="text-xs font-medium">{label}</div>
        {hint && <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}
